/**
 * ThemedRefreshControl — RefreshControl qui s'adapte au mode sombre
 *
 * Wrapper autour du RefreshControl natif qui applique automatiquement :
 *   - iOS : `tintColor` (couleur du spinner) selon le thème
 *   - Android : `colors` (le tableau de couleurs du spinner) selon le thème
 *
 * Sans ce wrapper, le spinner par défaut iOS est gris foncé → invisible sur
 * le fond noir du mode sombre. Avec ce wrapper, il devient doré/clair en
 * mode sombre et reste foncé en mode clair.
 *
 * Usage : drop-in replacement du RefreshControl natif.
 *
 *   <ScrollView
 *     refreshControl={
 *       <ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />
 *     }
 *   >
 *
 * Les props additionnelles (refreshing, onRefresh, etc.) sont passées tel
 * quel au RefreshControl natif.
 */
import React from 'react';
import { RefreshControl, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';

export default function ThemedRefreshControl(props) {
  const { dark, theme } = useTheme();

  // Couleur du spinner :
  //   - mode sombre  : doré clair (visible sur fond noir)
  //   - mode clair   : noir (visible sur fond blanc)
  // En mode sombre on utilise theme.accent (doré ~#D4B896) pour rester en
  // cohérence avec l'identité visuelle dorée de l'app.
  const tintColor = dark
    ? (theme.accent || '#D4B896')
    : (theme.text || '#1A1814');

  return (
    <RefreshControl
      {...props}
      // iOS : tintColor pilote la couleur du spinner ET du label éventuel
      tintColor={props.tintColor || tintColor}
      // Android : on passe un tableau (peut prendre plusieurs couleurs en alternance)
      colors={props.colors || [tintColor]}
      // Android : couleur de fond du conteneur du spinner
      progressBackgroundColor={
        props.progressBackgroundColor || (dark ? theme.bgCard : '#FFFFFF')
      }
      // iOS : titre stylé si title prop fourni
      titleColor={props.titleColor || tintColor}
    />
  );
}
